import { TEIQspinner, Btn } from './components/ui';
import { useUserContext } from './contexts/UserContext';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { signOut } from 'aws-amplify/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import { consumePostLoginPath } from './utils/postLoginPath';
import { AppRoutes } from './AppRoutes';
import { LoggedOutRoutes } from './LoggedOutRoutes';
import { Footer } from './components/Footer/Footer/Footer';
import { LeftNavigation } from './components/LeftNavigation';
import { AppShell } from './components/app-shell/AppShell';
import { MobileNavLogo } from './components/MobileNavLogo';

import './App.css';

import { useAppContext } from './contexts/AppContext';
import { useEffect, useRef } from 'react';
import { EquipmentProvider } from './contexts/EquipmentContext';
import { RegionAndLocationProvider } from './contexts/RegionAndLocationContext';
import { FormsProvider } from './contexts/FormsContext';
import { useRestoreSideNavOnDesktop } from './hooks/useRestoreSideNavOnDesktop';
import { useViewportHeight } from './hooks/useViewportHeight';

export const App = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isSideNavOpen, setIsSideNavOpen } = useAppContext();
    const { user, authStatus } = useAuthenticator((context) => {
        return [context.user, context.authStatus];
    });
    const { companyUser, companyOwner, businessAdmin, isLoading, isAccountDeactivated } = useUserContext();

    // const [isDarkMode, setIsDarkMode] = useState(false)
    const authorizedSiteUser = companyOwner || companyUser || businessAdmin;
    // Show full-page loader only for the initial Cognito/session restore — never unmount
    // an already-authorized app shell (e.g. Mac lock/unlock token refresh).
    const isAuthenticating =
        (authStatus === 'configuring' && !authorizedSiteUser) ||
        (user && !authorizedSiteUser && isLoading && !isAccountDeactivated);
    const showAppShell = Boolean(user && authorizedSiteUser);
    const appliedPostLoginPath = useRef(false);

    // The shell can replace the sign-in page before Login navigates. Send the
    // person to the in-app URL they opened while signed out.
    useEffect(() => {
        if (!showAppShell || appliedPostLoginPath.current) return;
        appliedPostLoginPath.current = true;
        const next = consumePostLoginPath();
        if (!next) return;
        const here = `${location.pathname}${location.search}`;
        if (here === next) return;
        navigate(next, { replace: true });
    }, [showAppShell, location.pathname, location.search, navigate]);

    useRestoreSideNavOnDesktop(showAppShell, setIsSideNavOpen);
    useViewportHeight(showAppShell);

    useEffect(() => {
        document.documentElement.style.setProperty(
            '--bg-image',
            'url(/images-background-options/water-high-opacity.png)',
        );
    }, []);

    const handleDeactivatedSignOut = async () => {
        await signOut();
        navigate('/signup', { replace: true });
    };

    return (
        <>
            <RegionAndLocationProvider>
                <FormsProvider>
                    <EquipmentProvider>
                            {isAuthenticating ? (
                                <div className="app-auth-loading">
                                    <TEIQspinner variant="lg" logoVariant="stacked" />
                                </div>
                            ) : user && isAccountDeactivated ? (
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minHeight: '100vh',
                                        padding: '2rem',
                                        textAlign: 'center',
                                    }}
                                >
                                    <h1 style={{ fontSize: '24px', color: '#1a365d', marginBottom: '12px' }}>
                                        Account Deactivated
                                    </h1>
                                    <p style={{ fontSize: '16px', color: '#64748b', maxWidth: '400px' }}>
                                        Your account has been deactivated by an administrator. Please contact your
                                        company admin if you believe this is an error.
                                    </p>
                                    <Btn
                                        variant="primary"
                                        buttonText="Sign Out"
                                        buttonOnClick={handleDeactivatedSignOut}
                                    />
                                </div>
                            ) : user && authorizedSiteUser ? (
                                <AppShell
                                    navigation={<LeftNavigation />}
                                    navigationOpen={isSideNavOpen}
                                    onNavigationChange={setIsSideNavOpen}
                                    footer={<Footer />}
                                    mobileLogo={<MobileNavLogo />}
                                >
                                    <AppRoutes />
                                </AppShell>
                            ) : (
                                <LoggedOutRoutes />
                            )}
                    </EquipmentProvider>
                </FormsProvider>
            </RegionAndLocationProvider>
        </>
    );
};
